import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Target, Flame, CheckCircle2, CalendarPlus, ClipboardCheck, Loader2, Pencil, Check, Clock, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PlanDialog, ScheduleDialog, CheckinDialog } from "@/components/AccountabilityDialogs";
import { ACTIVITY_STATUS_COLORS, CHECKIN_STATUS_COLORS, formatDate } from "@/data/accountability";
import api, { formatApiError } from "@/lib/api";

export function MemberAccountability() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planOpen, setPlanOpen] = useState(false);
  const [schedOpen, setSchedOpen] = useState(false);
  const [checkinActivity, setCheckinActivity] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/member/accountability");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const confirmPlan = async () => {
    setBusy(true);
    try {
      await api.post(`/plans/${data.plan.id}/confirm`);
      toast.success("Plan confirmed — it's now active!");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="py-10 grid place-items-center"><Loader2 className="w-6 h-6 text-[#E53E3E] animate-spin" /></div>;
  if (!data?.match) return null;

  const { plan, progress, activities = [], recent = [], match } = data;
  const upcoming = activities.filter((a) => a.status === "Scheduled");
  const canConfirm = plan && plan.status === "proposed" && plan.proposed_by !== "member";
  const awaitingOther = plan && plan.status === "proposed" && plan.proposed_by === "member";
  const pct = progress.weekly_target ? Math.min(100, Math.round((progress.this_week_completed / progress.weekly_target) * 100)) : 0;

  return (
    <div className="mt-8" data-testid="member-accountability">
      <h2 className="font-display text-2xl font-black uppercase tracking-tight mb-4">Your Accountability</h2>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Plan + progress */}
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-6" data-testid="member-plan-panel">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-bold uppercase tracking-tight flex items-center gap-2"><Target className="w-5 h-5 text-[#E53E3E]" /> Accountability Plan</h3>
              {plan && <Badge className={plan.status === "active" ? "bg-green-500/15 text-green-400" : "bg-amber-500/15 text-amber-400"}>{plan.status}</Badge>}
            </div>
            {plan ? (
              <div className="mt-4">
                <p className="text-xl font-semibold">{plan.goal}</p>
                <p className="text-sm text-zinc-400 mt-1 capitalize">{plan.activity?.replace("-", " ")} · {plan.target_per_week}× per week{plan.days?.length ? ` · ${plan.days.join(", ")}` : ""}{plan.preferred_time ? ` · ${plan.preferred_time}` : ""}</p>
                {plan.notes && <p className="mt-2 text-sm text-zinc-500">{plan.notes}</p>}
                <div className="mt-4 flex flex-wrap gap-2">
                  {canConfirm && <Button size="sm" onClick={confirmPlan} disabled={busy} className="bg-green-600 hover:bg-green-700 rounded-full" data-testid="member-confirm-plan"><Check className="w-4 h-4 mr-1" /> Confirm Plan</Button>}
                  <Button size="sm" variant="outline" onClick={() => setPlanOpen(true)} className="border-zinc-700 bg-transparent hover:bg-zinc-800 rounded-full" data-testid="member-edit-plan"><Pencil className="w-4 h-4 mr-1" /> {plan.status === "active" ? "Propose Changes" : "Edit"}</Button>
                </div>
                {awaitingOther && <p className="mt-3 text-xs text-amber-400">Waiting for your partner to confirm this plan.</p>}
              </div>
            ) : (
              <div className="mt-4">
                <p className="text-sm text-zinc-400">No accountability plan yet. Create one to start tracking your consistency.</p>
                <Button onClick={() => setPlanOpen(true)} className="mt-4 bg-[#E53E3E] hover:bg-[#C53030] rounded-full font-semibold h-11" data-testid="member-create-plan">Create Plan</Button>
              </div>
            )}
          </div>

          {/* This week progress */}
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-6">
            <h3 className="font-display text-lg font-bold uppercase tracking-tight">This Week</h3>
            <div className="mt-3 flex items-end gap-2">
              <span className="font-display text-4xl font-black" data-testid="member-week-progress">{progress.this_week_completed} / {progress.weekly_target || 0}</span>
              <span className="text-sm text-zinc-500 mb-1">activities completed</span>
            </div>
            <div className="mt-3 h-2.5 rounded-full bg-zinc-800 overflow-hidden"><div className="h-full bg-[#E53E3E] transition-all" style={{ width: `${pct}%` }} /></div>
            <div className="mt-5 grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2"><Flame className="w-5 h-5 text-[#E53E3E]" /><div><p className="font-display text-xl font-black" data-testid="member-streak">{progress.streak}</p><p className="text-xs text-zinc-500">week streak</p></div></div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-green-400" /><div><p className="font-display text-xl font-black">{progress.total_verified}</p><p className="text-xs text-zinc-500">verified total</p></div></div>
            </div>
          </div>

          {/* Upcoming activities */}
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-6" data-testid="member-upcoming-panel">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-bold uppercase tracking-tight flex items-center gap-2"><Clock className="w-5 h-5 text-[#E53E3E]" /> Upcoming Activities</h3>
              <Button size="sm" variant="outline" onClick={() => setSchedOpen(true)} className="border-zinc-700 bg-transparent hover:bg-zinc-800 rounded-full" data-testid="member-schedule-button"><CalendarPlus className="w-4 h-4 mr-1" /> Schedule</Button>
            </div>
            {upcoming.length === 0 ? (
              <p className="mt-4 text-sm text-zinc-500">No upcoming activities. Schedule one to get started.</p>
            ) : (
              <div className="mt-4 space-y-2">
                {upcoming.map((a) => (
                  <div key={a.id} className="flex items-center justify-between rounded-xl border border-zinc-800 bg-zinc-900/60 p-3" data-testid={`member-activity-${a.id}`}>
                    <div>
                      <p className="font-medium capitalize">{a.activity?.replace("-", " ")}</p>
                      <p className="text-xs text-zinc-500">{formatDate(a.date)}{a.time ? ` · ${a.time}` : ""}</p>
                      {a.checkin_status === "rejected" && <p className="text-xs text-[#E53E3E] mt-0.5">Rejected: {a.reject_reason}</p>}
                    </div>
                    {a.checkin_status === "pending" ? (
                      <Badge className="bg-amber-500/15 text-amber-400">Awaiting review</Badge>
                    ) : (
                      <Button size="sm" onClick={() => setCheckinActivity(a)} className="bg-[#E53E3E] hover:bg-[#C53030] rounded-full" data-testid={`member-checkin-${a.id}`}><ClipboardCheck className="w-4 h-4 mr-1" /> Check In</Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent history */}
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-6 h-fit" data-testid="member-history-panel">
          <h3 className="font-display text-lg font-bold uppercase tracking-tight flex items-center gap-2"><History className="w-5 h-5 text-[#E53E3E]" /> Recent Activity</h3>
          {recent.length === 0 ? (
            <p className="mt-4 text-sm text-zinc-500">No check-ins yet.</p>
          ) : (
            <div className="mt-4 space-y-2">
              {recent.map((c) => (
                <div key={c.id} className="flex items-center justify-between rounded-lg border border-zinc-800 bg-zinc-900/60 px-3 py-2">
                  <div><p className="text-sm font-medium capitalize">{c.activity?.replace("-", " ")}</p><p className="text-xs text-zinc-500">{formatDate(c.date)}</p></div>
                  <Badge className={CHECKIN_STATUS_COLORS[c.status]}>{c.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <PlanDialog open={planOpen} onOpenChange={setPlanOpen} matchId={match.id} plan={plan} onSaved={load} />
      <ScheduleDialog open={schedOpen} onOpenChange={setSchedOpen} matchId={match.id} defaultActivity={plan?.activity} onSaved={load} />
      <CheckinDialog open={!!checkinActivity} onOpenChange={(o) => !o && setCheckinActivity(null)} activity={checkinActivity} onSaved={load} />
    </div>
  );
}
